import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { SearchResult } from '@/types/search'
import { NodeListPage } from '@/components/nodes/NodeListPage'
import { useSearch } from '@/contexts/SearchContext'
import { SearchResultsGrouping } from '@/components/search/SearchResultsGrouping'
import { SearchResultsFilters } from '@/components/search/SearchResultsFilters'

// Define filter types for search results
export interface SearchFilters extends Record<string, string | number | symbol> {
  types: string // JSON string representation of the array
}

// Define grouping types for search results
export type SearchGroupBy = 'type' | 'none'

// Configuration for the search results page
const searchResultsConfig = {
  nodeName: 'result',
  nodeNamePlural: 'results',
  title: 'Search Results',
  description: 'Find users, projects, tasks, messages, and documents',
  searchPlaceholder: 'Search all nodes...',
  defaultTab: 'all',
  FilterComponent: SearchResultsFilters,
  GroupingComponent: SearchResultsGrouping,
  defaultGroupBy: 'type' as SearchGroupBy,
  defaultFilters: {
    types: JSON.stringify(['user', 'workspace', 'project', 'task', 'message', 'document']),
  },
  GroupingSelectorComponent: ({
    value,
    onChange,
  }: {
    value: SearchGroupBy
    onChange: (value: SearchGroupBy) => void
  }) => (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as SearchGroupBy)}
      className="rounded-md border border-input bg-background px-3 py-1 text-sm"
    >
      <option value="type">Group by type</option>
      <option value="none">No grouping</option>
    </select>
  ),
  dataPropName: 'nodes' as const,
  filterNodes: (
    nodes: SearchResult[],
    _activeTab: string | undefined,
    filters: SearchFilters,
    _searchQuery: string,
    _user?: { id: string; role: string } | undefined
  ) => {
    let filtered = nodes

    // Filter by node types if specified
    try {
      const types = JSON.parse(filters.types) as Array<
        'user' | 'workspace' | 'project' | 'task' | 'message' | 'document'
      >
      if (types.length > 0) {
        filtered = filtered.filter((node) => types.includes(node.type))
      }
    } catch {
      // If parsing fails, don't filter
    }

    // Additional filtering can be added here if needed
    return filtered
  },
  emptyStateIcon: undefined,
  emptyStateTitle: 'No search results found',
  emptyStateDescription: 'Try adjusting your search terms or filters',
}

export default function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const query = searchParams.get('q') || ''

  const { search, results, loading, clearSearch } = useSearch()

  // Create dynamic config with the search query in the title
  const dynamicConfig = {
    ...searchResultsConfig,
    title: query ? `Search Results for: "${query}"` : `Search Results for: ""`,
  }

  // State for the page
  const [searchQuery, setSearchQuery] = useState(query)
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState<SearchFilters>({
    types: JSON.stringify(['user', 'workspace', 'project', 'task', 'message', 'document']),
  } as SearchFilters)
  const [groupBy, setGroupBy] = useState<SearchGroupBy>('type')

  // Perform search when query changes
  useEffect(() => {
    if (query) {
      const types = filters.types
        ? (JSON.parse(filters.types) as Array<
            'user' | 'workspace' | 'project' | 'task' | 'message' | 'document'
          >)
        : undefined
      search(query, { types })
    }
  }, [query, filters.types, search])

  // Update URL when search query changes
  useEffect(() => {
    if (searchQuery !== query) {
      if (searchQuery) {
        setSearchParams({ q: searchQuery })
      } else {
        setSearchParams({})
      }
    }
  }, [searchQuery, query, setSearchParams])

  // Update local search query when URL changes (for browser back/forward)
  useEffect(() => {
    setSearchQuery(query)
  }, [query])

  const handleSearchChange = (newQuery: string) => {
    setSearchQuery(newQuery)
  }

  const handleFiltersChange = (newFilters: Partial<SearchFilters>) => {
    setFilters((prev) => {
      const updated = { ...prev, ...newFilters }
      // Ensure types is always a string
      if (updated.types === undefined) {
        updated.types = prev.types
      }
      return updated as SearchFilters
    })
  }

  const handleTabChange = (_tab: string) => {
    // For now, we only have one tab, but this could be extended
    // to filter by specific node types
  }

  // Clear search and reset to home page
  const handleClearSearch = () => {
    clearSearch()
    setSearchQuery('')
    setSearchParams({})
  }

  return (
    <NodeListPage
      nodes={results}
      loading={loading}
      searchQuery={searchQuery}
      onSearchChange={handleSearchChange}
      activeTab="all"
      onTabChange={handleTabChange}
      showFilters={showFilters}
      onFiltersToggle={() => setShowFilters(!showFilters)}
      filters={filters}
      onFiltersChange={handleFiltersChange}
      groupBy={groupBy}
      onGroupByChange={setGroupBy}
      config={dynamicConfig}
      disableSearchIntegration={true}
      emptyStateTitle="No results found"
      emptyStateDescription={
        searchQuery.trim()
          ? `No results found for "${searchQuery}". Try different search terms.`
          : 'Enter a search term to find users, projects, tasks, and more.'
      }
      additionalActions={
        searchQuery.trim() ? (
          <button
            onClick={handleClearSearch}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Clear search
          </button>
        ) : null
      }
    />
  )
}
